import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { MenuItem, Select, FormControl, InputLabel } from '@material-ui/core';
import TextField from 'components/TextField';
import Button from 'components/Button';
import { Icon } from 'styles/components';
import Images from 'assets/img';
import CollabsActions from 'store/ducks/collaborators';
import ProfilesActions from 'store/ducks/profiles';
import { schema } from '../components/Modal/schemaValidation';
import {
  Container,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  HeaderButtons,
  HeaderSaveButton,
  HeaderCancelButton,
  StepsContainer,
  Step,
  StepNumber,
  FormContent,
  LeftPanel,
  RightPanel,
  Section,
  SectionTitle,
  WrapperInput,
  AvatarSection,
  WrapperImage,
  Avatar,
  ImageText,
  InputFile,
  Loading,
  ReactiveText,
  Error,
  FullWidthSection,
} from './styles';

const STEPS = [
  { id: 1, label: 'Dados Pessoais' },
  { id: 2, label: 'Credenciais' },
];

function CollaboratorForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { collabs, isLoading } = useSelector((state) => state.collabs);
  const { profiles } = useSelector((state) => state.profiles);
  const isEdit = !!id;
  const formRef = useRef(null);
  const inputFile = useRef(null);
  
  const [avatar, setAvatar] = useState(null);
  const [email, setEmail] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const collaborator = isEdit ? collabs.find(c => c.id === id) : null;

  useEffect(() => {
    // Buscar perfis se não estiverem carregados
    if (!profiles || profiles.length === 0) {
      dispatch(ProfilesActions.getProfilesRequest());
    }

    // Se estiver editando e não tiver colaborador, buscar
    if (isEdit && !collaborator) {
      dispatch(CollabsActions.getCollabsRequest());
    }

    // Se estiver editando, carregar dados do colaborador
    if (isEdit && collaborator) {
      setEmail(collaborator.email || '');
      if (collaborator.profileImg || collaborator.avatar) {
        setAvatarPreview(collaborator.profileImg || collaborator.avatar);
      }
    }
  }, [dispatch, isEdit, id, collaborator, profiles]);

  function verifyEmail() {
    const user = collabs.find((item) => item.email === email);
    if (!user) return 0;
    if (isEdit && user.id === id) return 0; // Mesmo usuário editando
    return user.status === 0 ? 'inactive' : 'active';
  }

  const initialValues = {
    name: collaborator?.name || '',
    email: collaborator?.email || '',
    password: '',
    profile_id: collaborator?.profile_id || collaborator?.profile || '',
  };

  function handleSubmitted(values) {
    if (verifyEmail() === 'inactive') {
      dispatch(CollabsActions.reactivateCollabRequest(values.email, () => history.push('/collaborators')));
    } else {
      const data = {
        ...values,
        profile_id: values.profile_id || null, // Garantir que profile_id seja enviado
        avatar,
      };
      
      if (isEdit) {
        dispatch(CollabsActions.updateCollabRequest({ ...data, id }, () => history.push('/collaborators')));
      } else {
        dispatch(CollabsActions.addCollabRequest(data, () => history.push('/collaborators')));
      }
    }
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const activeProfiles = profiles?.filter(p => p.ativo !== false) || [];

  return (
    <Container>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {isEdit ? 'EDITAR COLABORADOR' : 'ADICIONAR COLABORADOR'}
          </ModalTitle>
          <HeaderButtons>
            <HeaderCancelButton onClick={() => history.push('/collaborators')}>
              Voltar
            </HeaderCancelButton>
            {isLoading ? (
              <Loading type="spin" width={20} height={20} />
            ) : (
              <HeaderSaveButton
                onClick={() => {
                  if (formRef.current) {
                    formRef.current.handleSubmit();
                  }
                }}
              >
                {verifyEmail() === 'inactive' ? 'Reativar' : 'Salvar'}
              </HeaderSaveButton>
            )}
          </HeaderButtons>
        </ModalHeader>

        <StepsContainer>
          {STEPS.map((step) => (
            <Step
              key={step.id}
              active={activeStep === step.id}
              onClick={() => setActiveStep(step.id)}
            >
              <StepNumber active={activeStep === step.id}>
                {step.id}.
              </StepNumber>
              {step.label}
            </Step>
          ))}
        </StepsContainer>

        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => handleSubmitted(values)}
          validationSchema={schema(verifyEmail(), isEdit)}
        >
          {({ values, handleChange, setFieldValue, errors, touched, handleSubmit: formikHandleSubmit }) => (
            <form onSubmit={formikHandleSubmit}>
              {activeStep === 1 && (
                <FormContent>
                  <FullWidthSection>
                    {verifyEmail() !== 'inactive' && (
                      <AvatarSection>
                        <WrapperImage>
                          {avatarPreview ? (
                            <Avatar src={avatarPreview} />
                          ) : (
                            <Icon height="35px" src={Images.IconCamera} />
                          )}
                        </WrapperImage>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <ImageText>
                            Adicione uma foto de perfil para o colaborador
                          </ImageText>
                          <Button
                            label="ENVIAR FOTO"
                            onClick={() => inputFile.current.click()}
                            type="button"
                          />
                        </div>
                      </AvatarSection>
                    )}
                  </FullWidthSection>

                  <LeftPanel>
                    <Section>
                      <SectionTitle>Dados Pessoais</SectionTitle>
                      {verifyEmail() !== 'inactive' && (
                        <WrapperInput>
                          <TextField
                            label="Nome *"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      )}
                      <WrapperInput>
                        <TextField
                          label="E-mail *"
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={(e) => {
                            handleChange(e);
                            setEmail(e.target.value);
                          }}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          fullWidth
                          color="secondary"
                        />
                      </WrapperInput>
                      {verifyEmail() === 'inactive' && (
                        <ReactiveText>
                          Esse e-mail já consta na nossa base de dados, mas está
                          desativado. Deseja reativá-lo?
                        </ReactiveText>
                      )}
                    </Section>
                  </LeftPanel>

                  <RightPanel>
                    <Section>
                      <SectionTitle>Perfil de Acesso</SectionTitle>
                      <WrapperInput>
                        <FormControl fullWidth error={touched.profile_id && Boolean(errors.profile_id)}>
                          <InputLabel></InputLabel>
                          <Select
                            name="profile_id"
                            value={values.profile_id}
                            onChange={(e) => {
                              setFieldValue('profile_id', e.target.value);
                            }}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Selecione um perfil</em>
                            </MenuItem>
                            {activeProfiles.map((profile) => (
                              <MenuItem key={profile.id} value={profile.id}>
                                {profile.nome}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.profile_id && errors.profile_id && (
                            <Error>{errors.profile_id}</Error>
                          )}
                        </FormControl>
                      </WrapperInput>
                    </Section>
                  </RightPanel>
                </FormContent>
              )}

              {activeStep === 2 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Credenciais</SectionTitle>
                      {verifyEmail() !== 'inactive' && (
                        <WrapperInput>
                          <TextField
                            label={isEdit ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                            name="password"
                            type="password"
                            value={values.password}
                            onChange={handleChange}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            fullWidth
                            color="secondary"
                          />
                        </WrapperInput>
                      )}
                    </Section>
                  </LeftPanel>
                </FormContent>
              )}
            </form>
          )}
        </Formik>
        <InputFile
          ref={inputFile}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleAvatarChange}
        />
      </ModalContainer>
    </Container>
  );
}

export default CollaboratorForm;
